import React, { useEffect, useState, useCallback } from 'react';
import './EditOrder.css';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './firebase';
import Navbar from './components/Navbar';
import { FaEdit, FaTrash, FaSave, FaCalendarAlt, FaMapMarkerAlt, FaUtensils, FaFilter, FaTimes, FaLock, FaDownload, FaSync } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const EditOrder = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // store original state for audit
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [postcodes, setPostcodes] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [venues, setVenues] = useState([]);
    const [numberOfDaysBlocking, setNumberOfDaysBlocking] = useState(0);
    const [loadError, setLoadError] = useState('');

    // Filter States
    const [filterDateType, setFilterDateType] = useState('all'); // 'all', 'individual', 'range'
    const [filterDate, setFilterDate] = useState('');
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [filterPostcode, setFilterPostcode] = useState('');
    const [filterAddress, setFilterAddress] = useState('');

    // Expanded row state
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        const fetchPriceBreakDown = async () => {
            const priceBreakDownRef = doc(db, 'PriceBreakDown', 'Prices');
            const priceSnap = await getDoc(priceBreakDownRef);
            if (priceSnap.exists()) {
                setNumberOfDaysBlocking(priceSnap.data().NumberOfDaysBlocking);
            } else {
                console.error('No PriceBreakDown document!');
            }
        };

        const fetchVenues = async () => {
            const user = auth.currentUser;
            if (user) {
                const uid = user.uid;
                const docRef = doc(db, "ClientContactInfo", uid);
                try {
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const venuesData = docSnap.data().venues || [];
                        setVenues(venuesData);
                        setPostcodes([...new Set(venuesData.map(venue => venue.postCode))]);
                        setAddresses([...new Set(venuesData.map(venue => venue.address))]);
                    }
                } catch (error) {
                    console.error("Error fetching venues:", error);
                }
            }
        };

        fetchPriceBreakDown();
        fetchVenues();
    }, [auth, db]);

    const fetchOrders = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) {
            alert("No user is signed in.");
            return;
        }
        setIsLoadingOrders(true);
        setLoadError('');
        const uid = user.uid;
        const docRef = doc(db, "ClientContactInfo", uid);
        try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const userOrders = docSnap.data().orders || [];
                setOrders(userOrders);
                setOriginalOrders(userOrders.map(o => ({ ...o }))); // deep copy original
            } else {
                setOrders([]);
                setOriginalOrders([]);
            }
            setIsLoaded(true);
        } catch (error) {
            console.error("Error fetching document:", error);
            setLoadError("Unable to load orders. Please try again.");
        } finally {
            setIsLoadingOrders(false);
        }
    }, [auth, db]);

    // Linked Filter Logic
    // Linked Filter Logic - Moved to handlers to avoid infinite loops
    const handlePostcodeChange = (e) => {
        const newPostcode = e.target.value;
        setFilterPostcode(newPostcode);

        if (newPostcode) {
            const linkedVenues = venues.filter(v => v.postCode === newPostcode);
            if (linkedVenues.length > 0) {
                const preferredAddress = linkedVenues[0].address || '';
                if (preferredAddress !== filterAddress) {
                    setFilterAddress(preferredAddress);
                }
            }
        } else {
            // Optional: clear address if postcode is cleared
            setFilterAddress('');
        }
    };

    const handleAddressChange = (e) => {
        const newAddress = e.target.value;
        setFilterAddress(newAddress);

        if (newAddress) {
            const linkedVenue = venues.find(v => v.address === newAddress);
            if (linkedVenue && linkedVenue.postCode !== filterPostcode) {
                setFilterPostcode(linkedVenue.postCode);
            }
        }
    };

    const clearFilters = () => {
        setFilterDateType('all');
        setFilterDate('');
        setFilterFrom('');
        setFilterTo('');
        setFilterPostcode('');
        setFilterAddress('');
    };

    // Filtering Logic
    const filteredOrders = orders.filter(order => {
        // 1. Postcode Filter
        if (filterPostcode && order.postcode !== filterPostcode) return false;

        // 2. Address Filter
        if (filterAddress && order.address !== filterAddress) return false;

        // 3. Date Filter
        if (filterDateType === 'individual' && filterDate) {
            if (order.dateType === 'individual') {
                return order.date === filterDate;
            } else if (order.dateType === 'range') {
                return filterDate >= order.from && filterDate <= order.to;
            }
        } else if (filterDateType === 'range' && filterFrom && filterTo) {
            if (order.dateType === 'individual') {
                return order.date >= filterFrom && order.date <= filterTo;
            } else if (order.dateType === 'range') {
                // Check for overlap
                return (order.from <= filterTo && order.to >= filterFrom);
            }
        }

        return true;
    });

    const isDateWithinBlockingPeriod = (date) => {
        if (!date) return false;
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Reset to start of day
        const orderDate = new Date(date);
        orderDate.setHours(0, 0, 0, 0); // Reset to start of day

        // Calculate days from now until order date
        const diffTime = orderDate - currentDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // If order date is in the past or within blocking period (4 days from now)
        return diffDays < numberOfDaysBlocking;
    };

    const formatTimestampPart = (value) => String(value).padStart(2, '0');
    const getAuditTimestamp = (inputDate = new Date()) => {
        const parsedDate = inputDate instanceof Date ? inputDate : new Date(inputDate);
        const safeDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
        return {
            iso: safeDate.toISOString(),
            date: `${safeDate.getFullYear()}-${formatTimestampPart(safeDate.getMonth() + 1)}-${formatTimestampPart(safeDate.getDate())}`,
            time: `${formatTimestampPart(safeDate.getHours())}:${formatTimestampPart(safeDate.getMinutes())}:${formatTimestampPart(safeDate.getSeconds())}`,
        };
    };

    const applyEditTimestamp = (order, editTimestamp) => ({
        ...order,
        orderLastEditedAt: editTimestamp.iso,
        orderLastEditedDate: editTimestamp.date,
        orderLastEditedTime: editTimestamp.time,
    });


    // Save audit log entries for each changed field
    const generateChangeLogs = (oldOrder, newOrder, userEmail, editTimestamp) => {
        const changeLog = newOrder.changeLog ? [...newOrder.changeLog] : [];
        const fieldsToCheck = [
            'dateType', 'date', 'from', 'to', 'postcode', 'address', 'totalMeals', 'specialMeals',
            'cutlery', 'cutleryQuantity', 'paperBowl', 'paperBowlQuantity', 'plasticContainer', 'plasticContainerQuantity',
            'packingInstructions'
        ];

        fieldsToCheck.forEach(field => {
            if (oldOrder[field] !== newOrder[field]) {
                changeLog.push({
                    field,
                    beforeValue: oldOrder[field],
                    afterValue: newOrder[field],
                    editedBy: userEmail,
                    editedAt: editTimestamp.iso,
                    editedDate: editTimestamp.date,
                    editedTime: editTimestamp.time,
                });
            }
        });

        return changeLog;
    };

    const cleanOrder = (order) => {
        const cleaned = { ...order };

        // Preserve existing identifiers - ONLY assign if they don't exist
        // This ensures order IDs remain stable across saves
        if (!cleaned.orderId) {
            cleaned.orderId = `oid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        }
        if (!cleaned.orderPlacedAt) {
            cleaned.orderPlacedAt = new Date().toISOString();
        }
        if (!cleaned.orderPlacedDate || !cleaned.orderPlacedTime) {
            const placedTimestamp = getAuditTimestamp(cleaned.orderPlacedAt);
            cleaned.orderPlacedDate = cleaned.orderPlacedDate || placedTimestamp.date;
            cleaned.orderPlacedTime = cleaned.orderPlacedTime || placedTimestamp.time;
        }
        if (cleaned.orderLastEditedAt && (!cleaned.orderLastEditedDate || !cleaned.orderLastEditedTime)) {
            const lastEditedTimestamp = getAuditTimestamp(cleaned.orderLastEditedAt);
            cleaned.orderLastEditedDate = cleaned.orderLastEditedDate || lastEditedTimestamp.date;
            cleaned.orderLastEditedTime = cleaned.orderLastEditedTime || lastEditedTimestamp.time;
        }

        ['totalMeals', 'specialMeals', 'cutleryQuantity', 'paperBowlQuantity', 'plasticContainerQuantity'].forEach(field => {
            const value = cleaned[field];
            if (value === undefined || value === null || value === '' || isNaN(Number(value))) {
                cleaned[field] = 0;
            } else {
                cleaned[field] = Number(value);
            }
        });

        ['packingInstructions', 'postcode', 'address', 'date', 'from', 'to'].forEach(field => {
            if (cleaned[field] === undefined || cleaned[field] === null) {
                cleaned[field] = '';
            } else {
                cleaned[field] = String(cleaned[field]);
            }
        });

        ['cutlery', 'paperBowl', 'plasticContainer', 'isApproved'].forEach(field => {
            if (cleaned[field] === undefined || cleaned[field] === null) {
                cleaned[field] = false;
            } else {
                cleaned[field] = Boolean(cleaned[field]);
            }
        });

        if (!cleaned.dateType) {
            cleaned.dateType = 'individual';
        } else {
            cleaned.dateType = String(cleaned.dateType);
        }

        Object.keys(cleaned).forEach(key => {
            if (cleaned[key] === undefined) {
                delete cleaned[key];
            }
        });

        return cleaned;
    };

    const hasDifferences = (a, b) => {
        const fields = [
            'dateType', 'date', 'from', 'to', 'postcode', 'address', 'totalMeals', 'specialMeals',
            'cutlery', 'cutleryQuantity', 'paperBowl', 'paperBowlQuantity', 'plasticContainer', 'plasticContainerQuantity',
            'packingInstructions', 'isApproved'
        ];
        return fields.some(field => (a?.[field] ?? '') !== (b?.[field] ?? ''));
    };

    const handleChange = (index, field, value) => {
        // We need to find the actual index in the main 'orders' array
        // because 'index' here comes from 'filteredOrders' map
        const orderToUpdate = filteredOrders[index];
        const realIndex = orders.findIndex(o => o === orderToUpdate);

        if (realIndex === -1) return;

        const updatedOrders = [...orders];

        // Handle number fields to prevent NaN - keep as string for input, convert on save
        if (['totalMeals', 'specialMeals', 'cutleryQuantity', 'paperBowlQuantity', 'plasticContainerQuantity'].includes(field)) {
            // Allow empty string or valid number string
            updatedOrders[realIndex][field] = value;
        } else {
            updatedOrders[realIndex][field] = value;
        }

        setOrders(updatedOrders);
    };

    const handleDeleteOrder = async (orderToDelete) => {
        if (isDateWithinBlockingPeriod(orderToDelete.date)) {
            alert("You can't delete orders within the blocking period. Please contact mealsupport@akshayapatra.org.uk.");
            return;
        }

        if (window.confirm("Are you sure you want to delete this order?")) {
            try {
                await httpsCallable(functions, 'deleteOrder')({ orderId: orderToDelete.orderId });
                setOrders(orders.filter(o => o !== orderToDelete));
                alert("Order deleted successfully!");
            } catch (error) {
                console.error("Error deleting order:", error);
                alert(error.message || "Failed to delete order.");
            }
        }
    };

    const handleSaveChanges = async () => {
        if (!isLoaded) {
            alert("Please load orders before saving changes.");
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            alert("No user is signed in.");
            return;
        }
        const uid = user.uid;
        const userEmail = user.email;

        const updatedOrdersWithLogs = orders.map((order, idx) => {
            const oldOrderRaw = originalOrders[idx] || {};
            const cleanedOldOrder = cleanOrder(oldOrderRaw);
            const cleanedOrder = cleanOrder(order);

            if (!hasDifferences(cleanedOldOrder, cleanedOrder)) {
                return { ...cleanedOrder, changeLog: oldOrderRaw.changeLog || [] };
            }

            const editTimestamp = getAuditTimestamp();
            const rawChangeLog = generateChangeLogs(cleanedOldOrder, cleanedOrder, userEmail, editTimestamp);
            const changeLog = rawChangeLog.map(entry => ({
                ...entry,
                beforeValue: entry.beforeValue ?? '',
                afterValue: entry.afterValue ?? '',
                editedBy: entry.editedBy || userEmail || 'User',
                editedAt: entry.editedAt || new Date().toISOString(),
                editedDate: entry.editedDate || getAuditTimestamp(entry.editedAt).date,
                editedTime: entry.editedTime || getAuditTimestamp(entry.editedAt).time,
            }));
            return applyEditTimestamp({ ...cleanedOrder, changeLog }, editTimestamp);
        });

        try {
            await httpsCallable(functions, 'saveOrders')({ orders: updatedOrdersWithLogs });
            alert("Orders saved successfully!");
            setOriginalOrders(updatedOrdersWithLogs.map(o => ({ ...o })));
        } catch (error) {
            console.error("Error saving orders:", error);
            alert(error.message || "Failed to save orders.");
        }
    };

    const handleSaveSingleOrder = async (order) => {
        if (!isLoaded) {
            alert("Please load orders before saving.");
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            alert("No user is signed in.");
            return;
        }
        const uid = user.uid;
        const userEmail = user.email;

        const realIndex = orders.indexOf(order);
        if (realIndex === -1) {
            alert("Unable to locate this order. Please reload and try again.");
            return;
        }

        const cleanedOldOrder = cleanOrder(originalOrders[realIndex] || {});
        const cleanedOrder = cleanOrder(order);

        if (!hasDifferences(cleanedOldOrder, cleanedOrder)) {
            alert("No changes to save for this order.");
            return;
        }

        const editTimestamp = getAuditTimestamp();
        const rawChangeLog = generateChangeLogs(cleanedOldOrder, cleanedOrder, userEmail, editTimestamp);
        const changeLog = rawChangeLog.map(entry => ({
            ...entry,
            beforeValue: entry.beforeValue ?? '',
            afterValue: entry.afterValue ?? '',
            editedBy: entry.editedBy || userEmail || 'User',
            editedAt: entry.editedAt || new Date().toISOString(),
            editedDate: entry.editedDate || getAuditTimestamp(entry.editedAt).date,
            editedTime: entry.editedTime || getAuditTimestamp(entry.editedAt).time,
        }));

        const updatedOrders = [...orders];
        updatedOrders[realIndex] = applyEditTimestamp({
            ...cleanedOrder,
            changeLog,
        }, editTimestamp);

        try {
            await httpsCallable(functions, 'saveOrders')({ orders: updatedOrders });
            alert("Order saved successfully!");
            setOrders(updatedOrders);
            setOriginalOrders(updatedOrders.map(o => ({ ...o })));
        } catch (error) {
            console.error("Error saving order:", error);
            alert(error.message || "Failed to save this order.");
        }
    };

    const filteredAddressOptions = filterPostcode
        ? [...new Set(venues.filter(v => v.postCode === filterPostcode).map(v => v.address).filter(Boolean))]
        : addresses;

    const handleLoadOrders = () => {
        fetchOrders();
    };

    const expandOrdersByDate = (ordersToExpand) => {
        const parseDate = (value) => value ? new Date(`${value}T00:00:00`) : null;
        const formatDate = (dateObj) => dateObj.toISOString().slice(0, 10);

        const expanded = [];
        ordersToExpand.forEach(order => {
            if (order.dateType === 'range') {
                const start = parseDate(order.from);
                const end = parseDate(order.to);
                if (!start || !end || start > end) {
                    return;
                }
                const cursor = new Date(start);
                while (cursor <= end) {
                    expanded.push({
                        ...order,
                        dateType: 'individual',
                        date: formatDate(cursor),
                    });
                    cursor.setDate(cursor.getDate() + 1);
                }
            } else {
                expanded.push(order);
            }
        });
        return expanded;
    };

    const handleExport = () => {
        if (!isLoaded) {
            alert("Please load orders before exporting.");
            return;
        }

        const expanded = expandOrdersByDate(filteredOrders);

        if (expanded.length === 0) {
            alert("No orders to export.");
            return;
        }

        const locationTotals = expanded.reduce((acc, order) => {
            const key = `${order.postcode || ''}|${order.address || ''}`;
            const meals = Number(order.totalMeals) || 0;
            acc[key] = (acc[key] || 0) + meals;
            return acc;
        }, {});

        const rows = expanded.map((order, idx) => {
            const key = `${order.postcode || ''}|${order.address || ''}`;
            return {
                'Sl. No.': idx + 1,
                'Venue Name': order.address || 'Venue',
                'Address': order.address || '',
                'Postcode': order.postcode || '',
                'Date': order.date || '',
                'Total Meals': Number(order.totalMeals) || 0,
                'Special Meals': Number(order.specialMeals) || 0,
                'Cutlery Qty': order.cutlery ? (Number(order.cutleryQuantity) || 0) : 0,
                'Paper Bowls Qty': order.paperBowl ? (Number(order.paperBowlQuantity) || 0) : 0,
                'Containers Qty': order.plasticContainer ? (Number(order.plasticContainerQuantity) || 0) : 0,
                'Location Total Meals': locationTotals[key] || 0,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
        XLSX.writeFile(workbook, `orders_export_${Date.now()}.xlsx`);
    };

    // ─── Derived stats for hero bar ───────────────────────────────────────────
    const today = new Date().toISOString().slice(0, 10);
    const totalMealsSum = filteredOrders.reduce((sum, o) => sum + (Number(o.totalMeals) || 0), 0);
    const upcomingCount = filteredOrders.filter(o => (o.date || o.from || '') > today).length;
    const pendingCount = filteredOrders.filter(o => !o.isApproved).length;

    // Active filter count badge
    const activeFilterCount = [
        filterPostcode,
        filterAddress,
        filterDateType !== 'all' ? filterDateType : '',
        filterDateType === 'individual' ? filterDate : '',
        filterDateType === 'range' ? filterFrom : '',
        filterDateType === 'range' ? filterTo : '',
    ].filter(Boolean).length;

    // Group filteredOrders by month for timeline
    const groupOrdersByMonth = (ordList) => {
        const groups = {};
        [...ordList]
            .sort((a, b) => {
                const da = a.date || a.from || '';
                const dateB = b.date || b.from || '';
                return da.localeCompare(dateB);
            })
            .forEach(order => {
                const dateStr = order.date || order.from || '';
                let label = 'Unknown';
                if (dateStr) {
                    const d = new Date(`${dateStr}T00:00:00`);
                    label = d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
                }
                if (!groups[label]) groups[label] = [];
                groups[label].push(order);
            });
        return groups;
    };

    const monthGroups = isLoaded ? groupOrdersByMonth(filteredOrders) : {};

    // Helper: format date badge parts
    const getDateBadgeParts = (order) => {
        const dateStr = order.date || order.from || '';
        if (!dateStr) return { day: '--', mon: '---' };
        const d = new Date(`${dateStr}T00:00:00`);
        const day = String(d.getDate()).padStart(2, '0');
        const mon = d.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
        return { day, mon };
    };

    return (
        <div className="mo-page">
            <Navbar title="Manage Orders" />

            {/* ── Hero Bar ─────────────────────────────────────────────────── */}
            <div className="mo-hero">
                <div className="mo-hero-left">
                    <h1 className="mo-hero-title">Manage Orders</h1>
                    <p className="mo-hero-sub">View, edit and track all your meal orders</p>
                </div>

                <div className="mo-hero-stats">
                    <div className="mo-stat-pill">
                        <span className="mo-stat-value">{filteredOrders.length}</span>
                        <span className="mo-stat-label">Total Orders</span>
                    </div>
                    <div className="mo-stat-pill">
                        <span className="mo-stat-value">{totalMealsSum}</span>
                        <span className="mo-stat-label">Total Meals</span>
                    </div>
                    <div className="mo-stat-pill mo-stat-pill--green">
                        <span className="mo-stat-value">{upcomingCount}</span>
                        <span className="mo-stat-label">Upcoming</span>
                    </div>
                    <div className="mo-stat-pill mo-stat-pill--amber">
                        <span className="mo-stat-value">{pendingCount}</span>
                        <span className="mo-stat-label">Pending</span>
                    </div>
                </div>

                <div className="mo-hero-actions">
                    <button
                        className="mo-btn mo-btn--green"
                        onClick={handleLoadOrders}
                        disabled={isLoadingOrders}
                    >
                        <FaSync className={isLoadingOrders ? 'mo-spin' : ''} />
                        {isLoadingOrders ? 'Loading…' : 'Load Orders'}
                    </button>
                    <button
                        className="mo-btn mo-btn--outline"
                        onClick={handleExport}
                        disabled={!isLoaded}
                    >
                        <FaDownload /> Export Excel
                    </button>
                </div>
            </div>

            {/* ── Filter Bar ───────────────────────────────────────────────── */}
            <div className="mo-filters">
                <div className="mo-filters-row">
                    <div className="mo-filter-field">
                        <label className="mo-filter-label">Postcode</label>
                        <select className="mo-filter-select" value={filterPostcode} onChange={handlePostcodeChange}>
                            <option value="">All Postcodes</option>
                            {postcodes.map((code, idx) => (
                                <option key={idx} value={code}>{code}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mo-filter-field">
                        <label className="mo-filter-label">Address</label>
                        <select className="mo-filter-select" value={filterAddress} onChange={handleAddressChange}>
                            <option value="">All Addresses</option>
                            {filteredAddressOptions.map((address, idx) => (
                                <option key={idx} value={address}>{address}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mo-filter-field">
                        <label className="mo-filter-label">Date Filter</label>
                        <select className="mo-filter-select" value={filterDateType} onChange={(e) => setFilterDateType(e.target.value)}>
                            <option value="all">All Dates</option>
                            <option value="individual">Specific Date</option>
                            <option value="range">Date Range</option>
                        </select>
                    </div>

                    {filterDateType === 'individual' && (
                        <div className="mo-filter-field">
                            <label className="mo-filter-label">Date</label>
                            <input
                                className="mo-filter-input"
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                            />
                        </div>
                    )}

                    {filterDateType === 'range' && (
                        <>
                            <div className="mo-filter-field">
                                <label className="mo-filter-label">From</label>
                                <input
                                    className="mo-filter-input"
                                    type="date"
                                    value={filterFrom}
                                    onChange={(e) => setFilterFrom(e.target.value)}
                                />
                            </div>
                            <div className="mo-filter-field">
                                <label className="mo-filter-label">To</label>
                                <input
                                    className="mo-filter-input"
                                    type="date"
                                    value={filterTo}
                                    onChange={(e) => setFilterTo(e.target.value)}
                                />
                            </div>
                        </>
                    )}

                    <div className="mo-filter-clear">
                        <button className="mo-btn mo-btn--ghost" onClick={clearFilters}>
                            <FaTimes /> Clear
                            {activeFilterCount > 0 && (
                                <span className="mo-filter-badge">{activeFilterCount}</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Timeline ─────────────────────────────────────────────────── */}
            <div className="mo-timeline">
                {!isLoaded && !isLoadingOrders && (
                    <div className="mo-empty-state">
                        <div className="mo-empty-icon"><FaCalendarAlt /></div>
                        <h3>No orders loaded yet</h3>
                        <p>Click <strong>Load Orders</strong> to view your orders</p>
                        <button className="mo-btn mo-btn--green" onClick={handleLoadOrders}>
                            <FaSync /> Load Orders
                        </button>
                    </div>
                )}

                {isLoadingOrders && (
                    <div className="mo-loading">
                        <div className="mo-spinner"></div>
                        <span>Loading your orders…</span>
                    </div>
                )}

                {loadError && (
                    <div className="mo-error-banner">
                        <FaTimes className="mo-error-icon" />
                        {loadError}
                    </div>
                )}

                {isLoaded && !isLoadingOrders && (
                    <>
                        {filteredOrders.length === 0 ? (
                            <div className="mo-empty-state">
                                <div className="mo-empty-icon"><FaFilter /></div>
                                <h3>No orders match your filters</h3>
                                <p>Try adjusting the filters above or clear them to see all orders.</p>
                                <button className="mo-btn mo-btn--ghost" onClick={clearFilters}>
                                    <FaTimes /> Clear Filters
                                </button>
                            </div>
                        ) : (
                            Object.entries(monthGroups).map(([month, monthOrders]) => (
                                <div key={month} className="mo-month-group">
                                    <div className="mo-month-label">
                                        <span className="mo-month-text">{month}</span>
                                        <span className="mo-month-badge">{monthOrders.length}</span>
                                    </div>

                                    {monthOrders.map((order, index) => {
                                        const filteredIndex = filteredOrders.indexOf(order);
                                        const isReadOnly = isDateWithinBlockingPeriod(order.date);
                                        const isExpanded = expandedId === order.orderId;
                                        const { day, mon } = getDateBadgeParts(order);
                                        const extras = [
                                            order.cutlery && `${order.cutleryQuantity || 0} cutlery`,
                                            order.paperBowl && `${order.paperBowlQuantity || 0} bowls`,
                                            order.plasticContainer && `${order.plasticContainerQuantity || 0} containers`,
                                        ].filter(Boolean);

                                        // Address options scoped to this order's postcode
                                        const orderAddressOptions = order.postcode
                                            ? [...new Set(venues.filter(v => v.postCode === order.postcode).map(v => v.address).filter(Boolean))]
                                            : addresses;

                                        return (
                                            <div
                                                key={order.orderId || index}
                                                className={`mo-row${isExpanded ? ' mo-row--expanded' : ''}${isReadOnly ? ' mo-row--locked' : ''}`}
                                            >
                                                {/* ── Collapsed head ── */}
                                                <div className="mo-row-head">
                                                    {/* Date badge */}
                                                    <div className={`mo-date-badge${order.isApproved ? ' mo-date-badge--green' : ' mo-date-badge--amber'}`}>
                                                        <span className="mo-date-day">{day}</span>
                                                        <span className="mo-date-mon">{mon}</span>
                                                        {isReadOnly && <FaLock className="mo-lock-overlay" />}
                                                    </div>

                                                    {/* Center info */}
                                                    <div className="mo-row-info">
                                                        <div className="mo-row-address">
                                                            <FaMapMarkerAlt className="mo-row-icon" />
                                                            <strong>{order.address || 'No address'}</strong>
                                                            {order.postcode && <span className="mo-postcode-chip">{order.postcode}</span>}
                                                        </div>
                                                        <div className="mo-row-chips">
                                                            <span className="mo-chip mo-chip--meals">
                                                                <FaUtensils /> {order.totalMeals || 0} meals
                                                            </span>
                                                            {Number(order.specialMeals) > 0 && (
                                                                <span className="mo-chip mo-chip--special">
                                                                    {order.specialMeals} special
                                                                </span>
                                                            )}
                                                            {extras.map((e, ei) => (
                                                                <span key={ei} className="mo-chip mo-chip--extra">{e}</span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Right actions */}
                                                    <div className="mo-row-actions">
                                                        <span className={`mo-status-badge${order.isApproved ? ' mo-status-badge--approved' : ' mo-status-badge--pending'}`}>
                                                            {order.isApproved ? 'Approved' : 'Pending'}
                                                        </span>
                                                        <button
                                                            className={`mo-icon-btn${isExpanded ? ' mo-icon-btn--active' : ''}`}
                                                            title={isExpanded ? 'Collapse' : 'Edit'}
                                                            onClick={() => setExpandedId(isExpanded ? null : order.orderId)}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="mo-icon-btn mo-icon-btn--danger"
                                                            title={isReadOnly ? 'Cannot delete within blocking period' : 'Delete Order'}
                                                            onClick={() => handleDeleteOrder(order)}
                                                            disabled={isReadOnly}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* ── Expanded edit form ── */}
                                                {isExpanded && (
                                                    <div className="mo-row-edit">
                                                        {isReadOnly && (
                                                            <div className="mo-lock-banner">
                                                                <FaLock /> This order is within the blocking period and cannot be edited. Contact mealsupport@akshayapatra.org.uk for changes.
                                                            </div>
                                                        )}

                                                        <div className="mo-edit-grid">
                                                            {/* Date */}
                                                            <div className="mo-edit-field">
                                                                <label className="mo-edit-label">Date</label>
                                                                <input
                                                                    className="mo-edit-input"
                                                                    type="date"
                                                                    value={order.date || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'date', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>

                                                            {/* Total Meals */}
                                                            <div className="mo-edit-field">
                                                                <label className="mo-edit-label">Total Meals</label>
                                                                <input
                                                                    className="mo-edit-input"
                                                                    type="number"
                                                                    value={order.totalMeals || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'totalMeals', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                    placeholder="e.g. 50"
                                                                />
                                                            </div>

                                                            {/* Special Meals */}
                                                            <div className="mo-edit-field">
                                                                <label className="mo-edit-label">Special Meals</label>
                                                                <input
                                                                    className="mo-edit-input"
                                                                    type="number"
                                                                    value={order.specialMeals || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'specialMeals', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                    placeholder="Dairy/Gluten Free"
                                                                />
                                                            </div>

                                                            {/* Postcode */}
                                                            <div className="mo-edit-field">
                                                                <label className="mo-edit-label">Postcode</label>
                                                                <select
                                                                    className="mo-edit-select"
                                                                    value={order.postcode || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'postcode', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                >
                                                                    <option value="">Select Postcode</option>
                                                                    {order.postcode && !postcodes.includes(order.postcode) && (
                                                                        <option value={order.postcode}>{order.postcode}</option>
                                                                    )}
                                                                    {postcodes.map((code, idx) => (
                                                                        <option key={idx} value={code}>{code}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            {/* Address */}
                                                            <div className="mo-edit-field mo-edit-field--wide">
                                                                <label className="mo-edit-label">Address</label>
                                                                <select
                                                                    className="mo-edit-select"
                                                                    value={order.address || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'address', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                >
                                                                    <option value="">Select Address</option>
                                                                    {order.address && !addresses.includes(order.address) && (
                                                                        <option value={order.address}>{order.address}</option>
                                                                    )}
                                                                    {orderAddressOptions.map((addr, idx) => (
                                                                        <option key={idx} value={addr}>{addr}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            {/* Extras checkboxes */}
                                                            <div className="mo-edit-field mo-edit-field--full">
                                                                <label className="mo-edit-label">Additional Items</label>
                                                                <div className="mo-extras-row">
                                                                    <label className="mo-extra-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={order.cutlery || false}
                                                                            onChange={(e) => handleChange(filteredIndex, 'cutlery', e.target.checked)}
                                                                            disabled={isReadOnly}
                                                                        />
                                                                        Cutlery
                                                                    </label>
                                                                    {order.cutlery && (
                                                                        <input
                                                                            className="mo-qty-input"
                                                                            type="number"
                                                                            value={order.cutleryQuantity || ''}
                                                                            onChange={(e) => handleChange(filteredIndex, 'cutleryQuantity', e.target.value)}
                                                                            disabled={isReadOnly}
                                                                            placeholder="Qty"
                                                                        />
                                                                    )}

                                                                    <label className="mo-extra-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={order.paperBowl || false}
                                                                            onChange={(e) => handleChange(filteredIndex, 'paperBowl', e.target.checked)}
                                                                            disabled={isReadOnly}
                                                                        />
                                                                        Paper Bowls
                                                                    </label>
                                                                    {order.paperBowl && (
                                                                        <input
                                                                            className="mo-qty-input"
                                                                            type="number"
                                                                            value={order.paperBowlQuantity || ''}
                                                                            onChange={(e) => handleChange(filteredIndex, 'paperBowlQuantity', e.target.value)}
                                                                            disabled={isReadOnly}
                                                                            placeholder="Qty"
                                                                        />
                                                                    )}

                                                                    <label className="mo-extra-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={order.plasticContainer || false}
                                                                            onChange={(e) => handleChange(filteredIndex, 'plasticContainer', e.target.checked)}
                                                                            disabled={isReadOnly}
                                                                        />
                                                                        Containers
                                                                    </label>
                                                                    {order.plasticContainer && (
                                                                        <input
                                                                            className="mo-qty-input"
                                                                            type="number"
                                                                            value={order.plasticContainerQuantity || ''}
                                                                            onChange={(e) => handleChange(filteredIndex, 'plasticContainerQuantity', e.target.value)}
                                                                            disabled={isReadOnly}
                                                                            placeholder="Qty"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Packing Instructions */}
                                                            <div className="mo-edit-field mo-edit-field--full">
                                                                <label className="mo-edit-label">Packing Instructions</label>
                                                                <textarea
                                                                    className="mo-edit-textarea"
                                                                    value={order.packingInstructions || ''}
                                                                    onChange={(e) => handleChange(filteredIndex, 'packingInstructions', e.target.value)}
                                                                    disabled={isReadOnly}
                                                                    placeholder="Special instructions for packing…"
                                                                    rows={2}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Edit action row */}
                                                        <div className="mo-edit-actions">
                                                            {!isReadOnly && (
                                                                <button
                                                                    className="mo-btn mo-btn--green"
                                                                    onClick={() => { handleSaveSingleOrder(order); setExpandedId(null); }}
                                                                >
                                                                    <FaSave /> Save This Order
                                                                </button>
                                                            )}
                                                            <button
                                                                className="mo-btn mo-btn--ghost"
                                                                onClick={() => setExpandedId(null)}
                                                            >
                                                                <FaTimes /> Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>

            {/* ── Sticky Save Bar ──────────────────────────────────────────── */}
            {isLoaded && (
                <div className="mo-save-bar">
                    <span className="mo-save-bar-info">
                        {orders.length} orders loaded &middot; {filteredOrders.length} filtered
                    </span>
                    <button className="mo-btn mo-btn--dark" onClick={handleSaveChanges}>
                        <FaSave /> Save All Changes
                    </button>
                </div>
            )}
        </div>
    );
};

export default EditOrder;
